import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faShippingFast, faCheck, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function ExternalCheckout({ cart, onOrderComplete, onCancel }) {
  const [orderData, setOrderData] = useState({
    endUserName: '',
    endUserEmail: '',
    endUserPhone: '',
    shippingName: '',
    shippingCompany: '',
    shippingAddress: '',
    shippingCity: '',
    shippingState: '',
    shippingZip: '',
    shippingCountry: 'US',
    shippingPhone: '',
    customerReference: '',
    specialInstructions: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { user } = useAuth();

  const validateForm = () => {
    const newErrors = {};

    // End user information validation
    if (!orderData.endUserName.trim()) {
      newErrors.endUserName = 'End user name is required';
    }
    if (!orderData.endUserEmail.trim()) {
      newErrors.endUserEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(orderData.endUserEmail)) {
      newErrors.endUserEmail = 'Email is invalid';
    }

    // Shipping information validation
    if (!orderData.shippingName.trim()) {
      newErrors.shippingName = 'Shipping name is required';
    }
    if (!orderData.shippingAddress.trim()) {
      newErrors.shippingAddress = 'Shipping address is required';
    }
    if (!orderData.shippingCity.trim()) {
      newErrors.shippingCity = 'City is required';
    }
    if (!orderData.shippingState.trim()) {
      newErrors.shippingState = 'State is required';
    }
    if (!orderData.shippingZip.trim()) {
      newErrors.shippingZip = 'ZIP code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setShowConfirmation(true);
  };

  const confirmOrder = async () => {
    setLoading(true);
    
    try {
      const orderPayload = {
        orderData,
        items: cart.map(item => ({
          idCatalog: item.idCatalog,
          itemNumber: item.itemNumber,
          itemName: item.itemName,
          quantity: item.quantity,
          unitWeight: item.unitWeight,
          totalWeight: item.totalWeight,
          notes: item.notes || ''
        }))
      };

      const response = await fetch(`${API_URL}/api/external-orders/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(orderPayload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        onOrderComplete(result);
      } else {
        throw new Error(result.message || 'Failed to create order');
      }

    } catch (error) {
      console.error('Error creating order:', error);
      setErrors({ submit: 'Failed to create order. Please try again.' });
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  const handleInputChange = (field, value) => {
    setOrderData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalWeight = () => {
    return cart.reduce((total, item) => total + (item.totalWeight || 0), 0).toFixed(2);
  };

  return (
    <Container className="py-4 checkout-container">
      <Row>
        <Col lg={8}>
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">
                <FontAwesomeIcon icon={faShippingFast} className="me-2" />
                Order Information
              </h4>
            </Card.Header>
            <Card.Body>
              {errors.submit && (
                <Alert variant="danger" className="mb-4">
                  {errors.submit}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                {/* End User Information */}
                <div className="mb-4">
                  <h5 className="border-bottom pb-2 mb-3">
                    <FontAwesomeIcon icon={faUser} className="me-2" />
                    End User Information
                  </h5>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Full Name *</Form.Label>
                        <Form.Control
                          type="text"
                          value={orderData.endUserName}
                          onChange={(e) => handleInputChange('endUserName', e.target.value)}
                          isInvalid={!!errors.endUserName}
                          placeholder="Enter full name"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.endUserName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email Address *</Form.Label>
                        <Form.Control
                          type="email"
                          value={orderData.endUserEmail}
                          onChange={(e) => handleInputChange('endUserEmail', e.target.value)}
                          isInvalid={!!errors.endUserEmail}
                          placeholder="Enter email address"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.endUserEmail}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          value={orderData.endUserPhone}
                          onChange={(e) => handleInputChange('endUserPhone', e.target.value)}
                          placeholder="Enter phone number"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Shipping Information */}
                <div className="mb-4">
                  <h5 className="border-bottom pb-2 mb-3">
                    <FontAwesomeIcon icon={faShippingFast} className="me-2" />
                    Shipping Information
                  </h5>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Recipient Name *</Form.Label>
                        <Form.Control
                          type="text"
                          value={orderData.shippingName}
                          onChange={(e) => handleInputChange('shippingName', e.target.value)}
                          isInvalid={!!errors.shippingName}
                          placeholder="Enter recipient name"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.shippingName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Company (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          value={orderData.shippingCompany}
                          onChange={(e) => handleInputChange('shippingCompany', e.target.value)}
                          placeholder="Enter company name"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label>Address *</Form.Label>
                        <Form.Control
                          type="text"
                          value={orderData.shippingAddress}
                          onChange={(e) => handleInputChange('shippingAddress', e.target.value)}
                          isInvalid={!!errors.shippingAddress}
                          placeholder="Enter full address"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.shippingAddress}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>City *</Form.Label>
                        <Form.Control
                          type="text"
                          value={orderData.shippingCity}
                          onChange={(e) => handleInputChange('shippingCity', e.target.value)}
                          isInvalid={!!errors.shippingCity}
                          placeholder="Enter city"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.shippingCity}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>State *</Form.Label>
                        <Form.Control
                          type="text"
                          value={orderData.shippingState}
                          onChange={(e) => handleInputChange('shippingState', e.target.value)}
                          isInvalid={!!errors.shippingState}
                          placeholder="Enter state"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.shippingState}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>ZIP Code *</Form.Label>
                        <Form.Control
                          type="text"
                          value={orderData.shippingZip}
                          onChange={(e) => handleInputChange('shippingZip', e.target.value)}
                          isInvalid={!!errors.shippingZip}
                          placeholder="Enter ZIP code"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.shippingZip}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          value={orderData.shippingPhone}
                          onChange={(e) => handleInputChange('shippingPhone', e.target.value)}
                          placeholder="Enter phone number"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Additional Information */}
                <div className="mb-4">
                  <h5 className="border-bottom pb-2 mb-3">Additional Information</h5>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Customer Reference</Form.Label>
                        <Form.Control
                          type="text"
                          value={orderData.customerReference}
                          onChange={(e) => handleInputChange('customerReference', e.target.value)}
                          placeholder="Enter reference number"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label>Special Instructions</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={orderData.specialInstructions}
                          onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                          placeholder="Enter any special instructions for this order"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className="d-flex justify-content-between">
                  <Button variant="outline-secondary" onClick={onCancel}>
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Back to Cart
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Submit Order for Approval
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="shadow-sm">
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">Order Summary</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <strong>Total Items: {getTotalItems()}</strong>
              </div>
              <div className="mb-3">
                <strong>Total Weight: {getTotalWeight()} lbs</strong>
              </div>
              
              <hr />
              
              <h6>Items:</h6>
              {cart.map((item) => (
                <div key={item.idCatalog} className="mb-2">
                  <div className="d-flex justify-content-between">
                    <small>{item.itemNumber}</small>
                    <small>Qty: {item.quantity}</small>
                  </div>
                  <small className="text-muted">{item.itemName}</small>
                </div>
              ))}
              
              <Alert variant="info" className="mt-3">
                <small>
                  Your order will be submitted for review and approval. 
                  You will receive a confirmation email once processed.
                </small>
              </Alert>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirmation Modal */}
      <Modal show={showConfirmation} onHide={() => setShowConfirmation(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Order Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to submit this order for approval?</p>
          <div className="bg-light p-3 rounded">
            <strong>Order Summary:</strong>
            <br />• {getTotalItems()} items
            <br />• Total weight: {getTotalWeight()} lbs
            <br />• Shipping to: {orderData.shippingName}, {orderData.shippingCity}, {orderData.shippingState}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmation(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmOrder} disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Submitting...
              </>
            ) : (
              'Yes, Submit Order'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ExternalCheckout; 